import * as React from "react";
import { Stack, useTheme, Grid, FormControl, InputLabel, MenuItem, Select, Box as MuiBox, Typography, Chip, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import Box from "@mui/material/Box";
import { useLocation } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import { IoMdEyeOff } from "react-icons/io";
import { formatDate, formatDateString } from "utilities/date";
import { currencyFormatter } from "utilities/currencyFormatter";
import moment from "moment";
import { usePaymentMethods } from "services/hooks/rentals";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: "#f1f1f5",
		color: "#000",
		fontSize: 15,
		fontWeight: 700,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 16,
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: "white",
	},
	"&:hover": {
		backgroundColor: theme.palette.action.hover,
	},
	"&:last-child td, &:last-child th": {
		border: 0,
	},
}));

const getPaymentStatusChip = (status) => {
	return (
		<Chip
			label={status ? 'Paid' : 'Pending'}
			color={status ? 'success' : 'warning'}
			size="small"
		/>
	);
};

const months = [
	{ month: "January", value: "January" },
	{ month: "February", value: "February" },
	{ month: "March", value: "March" },
	{ month: "April", value: "April" },
	{ month: "May", value: "May" },
	{ month: "June", value: "June" },
	{ month: "July", value: "July" },
	{ month: "August", value: "August" },
	{ month: "September", value: "September" },
	{ month: "October", value: "October" },
	{ month: "November", value: "November" },
	{ month: "December", value: "December" },
];


function TablePaginationActions(props) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (event) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (event) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (event) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (event) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
				{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
				{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

export default function PaymentsTable(props) {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	const [showDetails, setShowDetails] = React.useState(false);
	const [activeID, setActiveID] = React.useState(null);
	const currentMonth = moment().format("MMMM");
	const currentYear = moment().format("YYYY");
	const [filterMonth, setFilterMonth] = React.useState(currentMonth);
	const [filterYear, setFilterYear] = React.useState(currentYear);
	const [filterUnitName, setFilterUnitName] = React.useState("");
	const location = useLocation();
	const { paymentMethods } = usePaymentMethods();

	// Create a lookup map for payment methods by id (for legacy numeric format support)
	const paymentMethodsMap = React.useMemo(() => {
		const map = {};
		if (paymentMethods && Array.isArray(paymentMethods)) {
			paymentMethods.forEach(method => {
				if (method.id) {
					map[method.id] = method.name;
				}
			});
		}
		return map;
	}, [paymentMethods]);

	// Helper function to get payment method label
	const getPaymentMethodLabel = React.useCallback((method) => {
		// Handle new object structure: {id, name, code}
		if (method && typeof method === 'object' && method.name) {
			return method.name;
		}
		// Fallback for old numeric format (legacy support) - use hook data
		return paymentMethodsMap[method] || 'Unknown';
	}, [paymentMethodsMap]);

	// Extract unique years from the data
	const getUniqueYears = React.useMemo(() => {
		if (!props.rows || props.rows.length === 0) {
			return [];
		}

		const yearsSet = new Set();
		props.rows.forEach((row) => {
			if (row.date_paid) {
				const date = new Date(row.date_paid);
				const year = date.getFullYear();
				yearsSet.add(year);
			}
		});

		return Array.from(yearsSet).sort((a, b) => b - a);
	}, [props.rows]);

	// Extract unique unit names from the data
	const getUniqueUnitNames = React.useMemo(() => {
		if (!props.rows || props.rows.length === 0) {
			return [];
		}

		const unitNamesSet = new Set();
		props.rows.forEach((row) => {
			if (row.related_rental_unit && row.related_rental_unit.unit_name) {
				unitNamesSet.add(row.related_rental_unit.unit_name);
			}
		});

		return Array.from(unitNamesSet).sort();
	}, [props.rows]);

	// Filter and sort the rows based on selected filters
	const filteredRows = React.useMemo(() => {
		if (!props.rows || props.rows.length === 0) return [];

		const filtered = props.rows.filter((row) => {
			const datePaid = row.date_paid || row.date_paid_display;
			if (!datePaid) return false;

			const paymentDate = moment(datePaid);
			const paymentMonth = paymentDate.format("MMMM");
			const paymentYear = paymentDate.format("YYYY");

			// If "All" is selected for month or year, don't filter by that field
			const monthMatch = filterMonth === "All" || paymentMonth === filterMonth;
			const yearMatch = filterYear === "All" || paymentYear === filterYear;

			if (!monthMatch || !yearMatch) {
				return false;
			}

			// Filter by unit name
			if (filterUnitName) {
				if (!row.related_rental_unit || row.related_rental_unit.unit_name !== filterUnitName) {
					return false;
				}
			}

			return true;
		});

		// Sort by date_paid in descending order (newest first)
		return filtered.sort((a, b) => {
			const dateA = moment(a.date_paid || a.date_paid_display);
			const dateB = moment(b.date_paid || b.date_paid_display);

			// If dates are invalid, put them at the end
			if (!dateA.isValid() && !dateB.isValid()) return 0;
			if (!dateA.isValid()) return 1;
			if (!dateB.isValid()) return -1;

			// Sort in descending order (most recent first)
			return dateB.valueOf() - dateA.valueOf();
		});
	}, [props.rows, filterMonth, filterYear, filterUnitName]);

	// Calculate summary statistics based on filtered payments
	const totalAmount = React.useMemo(() => {
		return filteredRows.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
	}, [filteredRows]);

	const totalPayments = filteredRows.length;
	const paidPayments = filteredRows.filter(p => p.status).length;
	const pendingPayments = totalPayments - paidPayments;

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const toggleDetailsView = (rowId) => {
		setActiveID(rowId);
		setShowDetails(!showDetails);
	};

	const handleFilterMonthChange = (event) => {
		const newMonth = event.target.value;
		setFilterMonth(newMonth);
		setPage(0); // Reset to first page when filter changes

		// If both filters are set to "All", revert to current month/year
		if (newMonth === "All" && filterYear === "All") {
			setFilterMonth(currentMonth);
			setFilterYear(currentYear);
		}
	};

	const handleFilterYearChange = (event) => {
		const newYear = event.target.value;
		setFilterYear(newYear);
		setPage(0); // Reset to first page when filter changes

		// If both filters are set to "All", revert to current month/year
		if (filterMonth === "All" && newYear === "All") {
			setFilterMonth(currentMonth);
			setFilterYear(currentYear);
		}
	};

	const handleFilterUnitNameChange = (event) => {
		setFilterUnitName(event.target.value);
		setPage(0); // Reset to first page when filter changes
	};

	// Reset page if current page is beyond filtered results
	React.useEffect(() => {
		const maxPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
		if (page > maxPage) {
			setPage(0);
		}
	}, [filteredRows.length, rowsPerPage, page]);

	return (
		<>
			{/* Filter Controls */}
			<MuiBox sx={{ mb: 2, display: { xs: "none", md: "block" } }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
					<Typography variant="h5" sx={{ fontWeight: 'bold' }}>
						Tenant Payments
					</Typography>
					
					<Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							Filter:
						</Typography>
						<TextField
							label="Month"
							size="small"
							select
							value={filterMonth}
							onChange={handleFilterMonthChange}
							sx={{ minWidth: 150 }}
						>
							<MenuItem value="All">All Months</MenuItem>
							{months.map((month, index) => (
								<MenuItem key={index} value={month.month}>
									{month.month}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Year"
							size="small"
							select
							value={filterYear}
							onChange={handleFilterYearChange}
							sx={{ minWidth: 150 }}
						>
							<MenuItem value="All">All Years</MenuItem>
							{getUniqueYears.map((year, index) => (
								<MenuItem key={index} value={year.toString()}>
									{year}
								</MenuItem>
							))}
						</TextField>
						{getUniqueUnitNames.length > 0 && (
							<FormControl size="small" sx={{ minWidth: 200 }}>
								<InputLabel>Filter by Unit Name</InputLabel>
								<Select
									value={filterUnitName}
									label="Filter by Unit Name"
									onChange={handleFilterUnitNameChange}
								>
									<MenuItem value="">
										<em>All Units</em>
									</MenuItem>
									{getUniqueUnitNames.map((unitName) => (
										<MenuItem key={unitName} value={unitName}>
											{unitName}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					</Box>
				</Box>
			</MuiBox>

			{/* Mobile Filter Controls */}
			<MuiBox sx={{ mb: 2, display: { xs: "block", md: "none" } }}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Month"
							size="small"
							select
							value={filterMonth}
							onChange={handleFilterMonthChange}
						>
							<MenuItem value="All">All Months</MenuItem>
							{months.map((month, index) => (
								<MenuItem key={index} value={month.month}>
									{month.month}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Year"
							size="small"
							select
							value={filterYear}
							onChange={handleFilterYearChange}
						>
							<MenuItem value="All">All Years</MenuItem>
							{getUniqueYears.map((year, index) => (
								<MenuItem key={index} value={year.toString()}>
									{year}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					{getUniqueUnitNames.length > 0 && (
						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel>Filter by Unit Name</InputLabel>
								<Select
									value={filterUnitName}
									label="Filter by Unit Name"
									onChange={handleFilterUnitNameChange}
								>
									<MenuItem value="">
										<em>All Units</em>
									</MenuItem>
									{getUniqueUnitNames.map((unitName) => (
										<MenuItem key={unitName} value={unitName}>
											{unitName}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					)}
				</Grid>
			</MuiBox>

			{/* Summary Cards */}
			<MuiBox sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="primary" fontWeight="bold">
						{currencyFormatter(totalAmount)}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total Amount Collected
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="success.main" fontWeight="bold">
						{paidPayments}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Paid Payments
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="warning.main" fontWeight="bold">
						{pendingPayments}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Pending Payments
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="secondary" fontWeight="bold">
						{totalPayments}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total Payments
					</Typography>
				</Paper>
			</MuiBox>

			<TableContainer sx={{ display: { xs: "none", md: "block" } }} component={Paper}>
				<Table sx={{ minWidth: 650 }} aria-label="rental payments table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Payment ID</StyledTableCell>
							<StyledTableCell>Tenant Name</StyledTableCell>
							<StyledTableCell>Unit Name</StyledTableCell>
							<StyledTableCell>Property Name</StyledTableCell>
							<StyledTableCell>Amount</StyledTableCell>
							<StyledTableCell>Unit Rent</StyledTableCell>
							<StyledTableCell>Period</StyledTableCell>
							<StyledTableCell>Date Paid</StyledTableCell>
							<StyledTableCell>Payment Method</StyledTableCell>
							<StyledTableCell>Status</StyledTableCell>
							<StyledTableCell>Reference</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{props.isLoadingRentals ? (
							<StyledTableRow>
								<StyledTableCell colSpan={11} align="center">
									<Typography variant="body1" color="text.secondary">
										Loading...
									</Typography>
								</StyledTableCell>
							</StyledTableRow>
						) : (
							<>
								{filteredRows.length > 0 ? (
									filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
										<StyledTableRow key={row.id}>
											<TableCell>{row.id}</TableCell>
											<TableCell>
												{row.related_tenant?.first_name} {row.related_tenant?.last_name}
											</TableCell>
											<TableCell>{row.related_rental_unit?.unit_name}</TableCell>
											<TableCell>{row.related_rental_unit?.related_rental?.rental_name}</TableCell>
											<TableCell>
												<Typography fontWeight="bold">
													{currencyFormatter(parseFloat(row.amount || 0))}
												</Typography>
											</TableCell>
											<TableCell>
												{currencyFormatter(parseFloat(row.unit_rent || row.related_rental_unit?.rent || 0))}
											</TableCell>
											<TableCell>
												{row.related_month ? `${row.related_month.month} ${row.related_month.year}` : 'N/A'}
											</TableCell>
											<TableCell>
												{row.date_paid_display || formatDateString(row.date_paid) || 'N/A'}
											</TableCell>
											<TableCell>
												{getPaymentMethodLabel(row.payment_method)}
											</TableCell>
											<TableCell>
												{getPaymentStatusChip(row.status)}
											</TableCell>
											<TableCell>
												<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
													{row.external_reference || 'N/A'}
												</Typography>
											</TableCell>
										</StyledTableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={11} align="center">
											<Typography variant="body1" color="text.secondary">
												No rental payments found
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[10, 25, 50, 100]}
					component="div"
					count={filteredRows.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</TableContainer>

			{/* MOBILE */}
			<TableContainer sx={{ display: { xs: "block", md: "none" } }} component={Paper}>
				<Table aria-label="customized table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Tenant Name</StyledTableCell>
							<StyledTableCell align="left">Actions</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{props.isLoadingRentals ? (
							<StyledTableRow>
								<StyledTableCell colSpan={2} align="center">
									<Typography variant="body1" color="text.secondary">
										Loading...
									</Typography>
								</StyledTableCell>
							</StyledTableRow>
						) : (
							<>
								{filteredRows.length > 0 ? (
									(rowsPerPage > 0 ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : filteredRows).map((row) => (
										<StyledTableRow key={row.id}>
											<StyledTableCell align="left">
												<span>
													{row.related_tenant?.first_name} {row.related_tenant?.last_name}
												</span>
												<Stack spacing={1} style={{ fontSize: "16px", marginTop: "7px" }}>
													{activeID === row.id && showDetails ? (
														<>
															<span>
																<strong>Payment ID:</strong> {row.id}
															</span>
															<span>
																<strong>Amount:</strong> {currencyFormatter(parseFloat(row.amount || 0))}
															</span>
															<span>
																<strong>Unit Rent:</strong> {currencyFormatter(parseFloat(row.unit_rent || row.related_rental_unit?.rent || 0))}
															</span>
															<span>
																<strong>Property Name:</strong> {row.related_rental_unit?.related_rental?.rental_name}
															</span>
															<span>
																<strong>Unit name:</strong> {row.related_rental_unit?.unit_name}
															</span>
															<span>
																<strong>Period:</strong> {row.related_month ? `${row.related_month.month} ${row.related_month.year}` : 'N/A'}
															</span>
															<span>
																<strong>Date Paid:</strong> {row.date_paid_display || formatDateString(row.date_paid) || 'N/A'}
															</span>
															<span>
																<strong>Payment Method:</strong> {getPaymentMethodLabel(row.payment_method)}
															</span>
															<span>
																<strong>Status:</strong> {getPaymentStatusChip(row.status)}
															</span>
															<span>
																<strong>Reference:</strong> {row.external_reference || 'N/A'}
															</span>
														</>
													) : null}
												</Stack>
											</StyledTableCell>
											<StyledTableCell align="center">
												<span style={{ cursor: "pointer", display: "flex", alignItems: "center", fontSize: "15px" }} onClick={() => toggleDetailsView(row.id)}>
													See{" "}
													{activeID === row.id && showDetails ? (
														<>
															{" "}
															less <IoMdEyeOff style={{ marginLeft: "5px", marginTop: "1px" }} />{" "}
														</>
													) : (
														<>
															{" "}
															more <FaRegEye style={{ marginLeft: "5px", marginTop: "1px" }} />{" "}
														</>
													)}
												</span>
											</StyledTableCell>
										</StyledTableRow>
									))
								) : (
									<StyledTableRow>
										<StyledTableCell colSpan={2} align="center">
											<Typography variant="body1" color="text.secondary">
												No rental payments found
											</Typography>
										</StyledTableCell>
									</StyledTableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[10, 25, 50, 100]}
					component="div"
					count={filteredRows.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</TableContainer>
		</>
	);
}
